import React, { useState } from "react";
import {
    FormGroup,
    FormControl,
    FormLabel,
    TextField,
    FormHelperText,
    Typography,
    CircularProgress,
    Link
} from "@mui/material";
import { SubmitButton } from "modules/common/components/forms";
import { AuthActionCreators } from "modules/auth/actionCreator";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";

interface TwoFactorFormProps {
    returnUrl?: string;
}

export default function TwoFactorForm({ returnUrl }: TwoFactorFormProps) {
    const [otpInput, setOtpInput] = useState("");
    const dispatch = useAppDispatch();
    const {
        login: { message, isVerifyingOtp, isResendingOtp, maskedEmail, otpResendMessage }
    } = useAppSelector((state) => state.auth);

    const verify = () => {
        dispatch(AuthActionCreators.verifyOtp(otpInput.trim(), returnUrl));
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        verify();
    };

    const resend = () => {
        setOtpInput("");
        dispatch(AuthActionCreators.resendOtp());
    };

    return (
        <form className="two-factor-form" onSubmit={handleFormSubmit}>
            <Typography className="two-factor-heading" variant="h5" component="h2">
                Verify Your Account
            </Typography>
            <FormGroup>
                <Typography variant="body1">
                    We&apos;ve sent a two-factor authentication code to <strong>{maskedEmail}</strong>. To proceed,
                    please use the code below <strong>within 10 minutes</strong>.
                </Typography>
                <br />
                <Typography variant="body1">
                    If you don&apos;t use the code before it expires, you&apos;ll need to request a new code.
                </Typography>
                <FormControl className="form-row">
                    <FormLabel>Verification Code</FormLabel>
                    <TextField
                        variant="standard"
                        value={otpInput}
                        onChange={(evt) => setOtpInput(evt.target.value)}
                        inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                        autoFocus
                    />
                </FormControl>
                <FormHelperText component="div">
                    {isResendingOtp ? (
                        <CircularProgress className="resend-otp-spinner" size={16} aria-label="Sending a new code" />
                    ) : (
                        <Link
                            id="resend-otp-link"
                            className="resend-otp-link"
                            component="button"
                            type="button"
                            onClick={resend}>
                            Need a new code?
                        </Link>
                    )}
                </FormHelperText>
                {otpResendMessage ? (
                    <FormHelperText id="otp-info" className="info-text">
                        {otpResendMessage}
                    </FormHelperText>
                ) : null}
                {message ? (
                    <FormHelperText id="otp-error" className="error-text">
                        {message}
                    </FormHelperText>
                ) : null}
                <FormControl className="form-row">
                    <SubmitButton
                        type="submit"
                        className="verify-button"
                        color="primary"
                        variant="contained"
                        isSubmitting={isVerifyingOtp}
                        disabled={isVerifyingOtp || !otpInput.trim()}
                        submittingMessage="Verifying">
                        Verify
                    </SubmitButton>
                </FormControl>
            </FormGroup>
        </form>
    );
}
